import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime } from 'rxjs';

const SYSTEM_DEFAULT = 'System Default';
const BOTTOM_RIGHT = 'bottom-right';
const BOTTOM_LEFT = 'bottom-left';
const TOP_RIGHT = 'top-right';
const TOP_LEFT = 'top-left';
const TRANSITION_ALL_EASE = 'all 0.2s ease';
const BG_COLOR = 'background-color';
const COLOR = 'color';
const FONT_FAMILY = 'font-family';
const TRANSITION = 'transition';
const INHERIT = 'inherit';
const MEDIUM = 'medium';
const PILL = 'pill';

export const CHATBOT_FONT_OPTIONS = [
  SYSTEM_DEFAULT,
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
] as const;

@Component({
  selector: 'app-chatbot-customization',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule],
  templateUrl: './chatbot-customization.component.html',
  styleUrls: ['./chatbot-customization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotCustomizationComponent implements OnInit {
  /** The `step8` FormGroup passed in from the parent bot-setup component. */
  @Input({ required: true }) group!: FormGroup;
  @ViewChild('logoFileInput') logoFileInputRef?: ElementRef<HTMLInputElement>;

  // ── Collapsible section state ──────────────────────────────────────────────
  chatWindowOpen = true;
  chatLauncherOpen = true;

  // ── Logo upload state ──────────────────────────────────────────────────────
  previewLogo: string | ArrayBuffer | null = null;
  logoFileName: string | null = null;

  // ── Debounced Preview state ────────────────────────────────────────────────
  debouncedPreviewStyle: Record<string, string> = {};
  debouncedHeaderStyle: Record<string, string> = {};
  debouncedChatAreaStyle: Record<string, string> = {};
  debouncedBotBubbleStyle: Record<string, string> = {};
  debouncedUserBubbleStyle: Record<string, string> = {};
  readonly bubbleFontSize = signal('14px');
  debouncedLabel = 'Schedule Service';

  // ── Services ───────────────────────────────────────────────────────────────
  private readonly cd = inject(ChangeDetectorRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // When editing an existing bot the parent patches `headerLogo` asynchronously
    // after `getChatbotCustomization` resolves.  Seed the preview from whatever
    // is already in the control and keep it in sync going forward.
    const logoCtrl = this.group?.get('headerLogo');
    if (!logoCtrl) return;

    const initial = logoCtrl.value;
    if (initial && typeof initial === 'string') {
      this.previewLogo = initial;
    }

    // Populate initial debounced styles
    this.updatePreview();

    logoCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val: string | null) => {
      // External patch (loadChatbotCustomization) → restore preview
      if (val && typeof val === 'string' && !this.previewLogo) {
        this.previewLogo = val;
        this.updatePreview();
        this.cd.markForCheck();
      }
      // Reset (parent calls resetChatbotCustomizationStep) → clear preview
      if (!val) {
        this.previewLogo = null;
        this.logoFileName = null;
        if (this.logoFileInputRef?.nativeElement) {
          this.logoFileInputRef.nativeElement.value = '';
        }
        this.updatePreview();
        this.cd.markForCheck();
      }
    });

    // Subscribe to form value changes with 200ms debounce
    this.group.valueChanges.pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updatePreview();
      this.cd.markForCheck();
    });
  }

  updatePreview(): void {
    this.debouncedPreviewStyle = this.getPreviewStyle();
    this.debouncedHeaderStyle = this.getPreviewHeaderStyle();
    this.debouncedChatAreaStyle = this.getPreviewChatAreaStyle();
    this.debouncedBotBubbleStyle = this.getPreviewBotBubbleStyle();
    this.debouncedUserBubbleStyle = this.getPreviewUserBubbleStyle();
    const size = this.cv('chatBubbleTextSize') || MEDIUM;
    this.bubbleFontSize.set(size === 'small' ? '12px' : size === 'large' ? '16px' : '14px');
    this.debouncedLabel = this.cv('chatButtonLabel') || 'Schedule Service';

    const headerFont = this.cv('headerFont');
    const bubbleFont = this.cv('chatBubbleFont');
    const buttonFont = this.cv('buttonFont');
    if (headerFont) this.loadCustomFont(headerFont);
    if (bubbleFont) this.loadCustomFont(bubbleFont);
    if (buttonFont) this.loadCustomFont(buttonFont);
  }

  private loadCustomFont(fontName: string): void {
    if (!fontName || fontName === SYSTEM_DEFAULT) return;
    const linkId = `gfont-${fontName.toLowerCase().replace(/\s+/g, '-')}`;
    if (document.getElementById(linkId)) return;

    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      fontName
    )}:wght@300;400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
  }

  // ── Static option arrays ───────────────────────────────────────────────────
  readonly fontOptions = CHATBOT_FONT_OPTIONS;

  readonly placementOptions = [
    { id: BOTTOM_RIGHT, label: 'Bottom Right', arrow: '↘' },
    { id: BOTTOM_LEFT, label: 'Bottom Left', arrow: '↙' },
    { id: TOP_RIGHT, label: 'Top Right', arrow: '↗' },
    { id: TOP_LEFT, label: 'Top Left', arrow: '↖' },
  ];

  readonly textSizeOptions = [
    { value: 'small', label: 'Small (12px)' },
    { value: MEDIUM, label: 'Medium (14px)' },
    { value: 'large', label: 'Large (16px)' },
  ];

  readonly buttonSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: MEDIUM, label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'custom', label: 'Custom' },
  ];

  readonly fontWeightOptions = [
    { value: 'light', label: 'Light' },
    { value: 'regular', label: 'Regular' },
    { value: MEDIUM, label: 'Medium' },
    { value: 'semibold', label: 'Semibold' },
    { value: 'bold', label: 'Bold' },
    { value: 'extrabold', label: 'Extrabold' },
  ];

  readonly buttonStyleOptions = [
    { value: PILL, label: 'Pill' },
    { value: 'rounded', label: 'Rounded' },
    { value: 'square', label: 'Square' },
  ];

  // ── Getters for toggle active-state bindings ───────────────────────────────

  get ctrlChatBubbleTextSize(): string {
    return this.cv('chatBubbleTextSize') ?? MEDIUM;
  }
  get ctrlChatButtonSize(): string {
    return this.cv('chatButtonSize') ?? MEDIUM;
  }
  get ctrlFontWeight(): string {
    return this.cv('fontWeight') ?? 'semibold';
  }
  get ctrlButtonStyle(): string {
    return this.cv('buttonStyle') ?? PILL;
  }
  get ctrlButtonPlacement(): string {
    return this.cv('buttonPlacement') ?? BOTTOM_RIGHT;
  }
  get ctrlAdvancedMarginOn(): boolean {
    return !!this.cv('advancedMarginControl');
  }

  /** Current character count of the button label. */
  get ctrlLabelLength(): number {
    return ((this.cv('chatButtonLabel') as string) ?? '').length;
  }

  // ── Template helper — reads a raw value from the form group ───────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cv(name: string): any {
    return this.group?.get(name)?.value ?? '';
  }

  // ── Color-picker handlers ─────────────────────────────────────────────────

  /** Called by the native <input type="color"> when the user picks a colour. */
  onColorInput(event: Event, controlName: string): void {
    const value = (event.target as HTMLInputElement).value;
    const control = this.group.get(controlName);
    if (!control || control.value === value) return;
    control.setValue(value);
    control.markAsDirty();
    this.cd.markForCheck();
  }

  // ── Toggle-button handlers ────────────────────────────────────────────────

  setToggle(controlName: string, value: string): void {
    const control = this.group.get(controlName);
    if (!control) return;
    control.setValue(value);
    control.markAsDirty();
    this.updatePreview();
    this.cd.detectChanges();
  }

  toggleAdvancedMargin(): void {
    const control = this.group.get('advancedMarginControl');
    if (!control) return;
    control.setValue(!control.value);
    control.markAsDirty();
    this.cd.markForCheck();
  }

  // ── Logo upload handlers ──────────────────────────────────────────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'svg') {
      const reader = new FileReader();
      reader.onload = (e): void => {
        this.previewLogo = e.target?.result ?? null;
        this.logoFileName = file.name;
        this.patchLogo(this.previewLogo);
      };
      reader.readAsDataURL(file);
      return;
    }

    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e): void => {
      img.src = (e.target?.result as string) ?? '';
      img.onload = (): void => {
        if (img.width === 500 && img.height === 500) {
          this.previewLogo = img.src;
          this.logoFileName = file.name;
          this.patchLogo(this.previewLogo);
        } else {
          this.snackBar.open(`Image must be 500×500 px — got ${img.width}×${img.height} px.`, 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
          });
          this.clearLogo();
        }
      };
    };
    reader.readAsDataURL(file);
  }

  removeLogo(event: Event): void {
    event.stopPropagation();
    this.clearLogo();
  }

  // ── Preview style ─────────────────────────────────────────────────────────

  private _getButtonSizeStyles(): { padding: string; width: string; height: string } {
    const size = this.cv('chatButtonSize') || MEDIUM;
    if (size === 'small') {
      return { padding: '6px 12px', width: '', height: '' };
    }
    if (size === 'large') {
      return { padding: '14px 28px', width: '', height: '' };
    }
    if (size === 'custom') {
      const cw = Number(this.cv('chatButtonWidth')) || 0;
      const ch = Number(this.cv('chatButtonHeight')) || 0;
      if (cw > 0 && ch > 0) {
        return { padding: '0', width: `${cw}px`, height: `${ch}px` };
      }
    }
    return { padding: '10px 20px', width: '', height: '' };
  }

  private _getButtonPlacementStyles(): Record<string, string> {
    const placement = this.cv('buttonPlacement') || BOTTOM_RIGHT;
    const BASE = 12;
    const horiz = BASE + Math.max(0, Math.min(Number(this.cv('horizontalOffset') ?? 0), 80));
    const vert = BASE + Math.max(0, Math.min(Number(this.cv('verticalOffset') ?? 0), 80));

    const hpx = `${horiz}px`;
    const vpx = `${vert}px`;

    const pos: Record<string, string> = { position: 'absolute' };
    if (placement === BOTTOM_RIGHT) {
      pos['bottom'] = vpx;
      pos['right'] = hpx;
      pos['top'] = 'auto';
      pos['left'] = 'auto';
    } else if (placement === BOTTOM_LEFT) {
      pos['bottom'] = vpx;
      pos['left'] = hpx;
      pos['top'] = 'auto';
      pos['right'] = 'auto';
    } else if (placement === TOP_RIGHT) {
      pos['top'] = vpx;
      pos['right'] = hpx;
      pos['bottom'] = 'auto';
      pos['left'] = 'auto';
    } else if (placement === TOP_LEFT) {
      pos['top'] = vpx;
      pos['left'] = hpx;
      pos['bottom'] = 'auto';
      pos['right'] = 'auto';
    }
    return pos;
  }

  private _getFontFamily(font: string): string {
    return font === SYSTEM_DEFAULT ? INHERIT : `'${font}', sans-serif`;
  }

  private _getFontWeight(weight: string): string {
    const weightMap: Record<string, string> = {
      light: '300',
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
    };
    return weightMap[weight] ?? '600';
  }

  private _getFontSize(btnSize: string): string {
    if (btnSize === 'small') return '12px';
    if (btnSize === 'large') return '16px';
    return '14px';
  }

  private _getBorderRadius(btnStyle: string): string {
    if (btnStyle === PILL) return '30px';
    if (btnStyle === 'rounded') return '8px';
    return '0px';
  }

  private _getButtonGap(): string {
    const label = (this.cv('chatButtonLabel') || '').trim();
    return this.previewLogo && label ? '8px' : '0px';
  }

  getPreviewStyle(): Record<string, string> {
    // ── Background & text color (invalid hex → fallback default) ───────────
    const bg = this.safeColor(this.cv('chatButtonColor'), '#1a2a4a');
    const color = this.safeColor(this.cv('chatButtonTextColor'), '#FFFFFF');

    // ── Font family ─────────────────────────────────────────────────────────
    const family = this._getFontFamily(this.cv('buttonFont') || SYSTEM_DEFAULT);

    // ── Font weight ─────────────────────────────────────────────────────────
    const fw = this._getFontWeight(this.cv('fontWeight') || 'semibold');

    // ── Font size (driven by chatButtonSize) ────────────────────────────────
    const fs = this._getFontSize(this.cv('chatButtonSize') || MEDIUM);

    // ── Border radius ───────────────────────────────────────────────────────
    const radius = this._getBorderRadius(this.cv('buttonStyle') || PILL);

    const sizeStyles = this._getButtonSizeStyles();
    const pos = this._getButtonPlacementStyles();

    // ── Compose ─────────────────────────────────────────────────────────────
    const styles: Record<string, string> = {
      [BG_COLOR]: bg,
      [COLOR]: color,
      [FONT_FAMILY]: family,
      'font-weight': fw,
      'font-size': fs,
      'border-radius': radius,
      'padding': sizeStyles.padding,
      'border': 'none',
      'box-shadow': '0 4px 14px rgba(0,0,0,0.18)',
      'display': 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'cursor': 'default',
      'user-select': 'none',
      'white-space': 'nowrap',
      'letter-spacing': '0.01em',
      [TRANSITION]: TRANSITION_ALL_EASE,
      'gap': this._getButtonGap(),
      'box-sizing': 'border-box',
      ...pos,
    };

    if (sizeStyles.width) styles['width'] = sizeStyles.width;
    if (sizeStyles.height) styles['height'] = sizeStyles.height;

    return styles;
  }

  /** Returns `value` if it is a valid 6-digit hex colour, otherwise `fallback`. */
  private safeColor(value: string, fallback: string): string {
    return /^#[0-9A-Fa-f]{6}$/.test(value ?? '') ? value : fallback;
  }

  // ── Preview sub-section styles (chat header + chat area + bubbles) ────────

  /** Chat header bar: background, text colour, font family. */
  getPreviewHeaderStyle(): Record<string, string> {
    const bg = this.safeColor(this.cv('headerBackgroundColor'), '#EEEEEE');
    const color = this.safeColor(this.cv('headerTextColor'), '#FFFFFF');
    const font = this.cv('headerFont') || SYSTEM_DEFAULT;
    return {
      [BG_COLOR]: bg,
      [COLOR]: color,
      [FONT_FAMILY]: font === SYSTEM_DEFAULT ? INHERIT : `'${font}', sans-serif`,
      [TRANSITION]: TRANSITION_ALL_EASE,
    };
  }

  /** Chat area backdrop behind both sample bubbles. */
  getPreviewChatAreaStyle(): Record<string, string> {
    return {
      [BG_COLOR]: this.safeColor(this.cv('chatBubbleBackgroundColor'), '#607785'),
    };
  }

  /** Bot message bubble (left-aligned). Uses `botMessageBubbleColor` form control. */
  getPreviewBotBubbleStyle(): Record<string, string> {
    return this.buildBubbleStyle('botMessageBubbleColor');
  }

  /** Customer message bubble (right-aligned). Uses `customerMessageBubbleColor` form control. */
  getPreviewUserBubbleStyle(): Record<string, string> {
    return this.buildBubbleStyle('customerMessageBubbleColor');
  }

  private buildBubbleStyle(bgControl: string): Record<string, string> {
    const bg = this.safeColor(this.cv(bgControl), '#888888');
    const color = this.safeColor(this.cv('chatBubbleTextColor'), '#FFFFFF');
    const font = this.cv('chatBubbleFont') || SYSTEM_DEFAULT;
    const size = this.cv('chatBubbleTextSize') || MEDIUM;
    const fontSize = size === 'small' ? '12px' : size === 'large' ? '16px' : '14px';
    return {
      [BG_COLOR]: bg,
      [COLOR]: color,
      [FONT_FAMILY]: font === SYSTEM_DEFAULT ? INHERIT : `'${font}', sans-serif`,
      'font-size': fontSize,
      [TRANSITION]: TRANSITION_ALL_EASE,
    };
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private patchLogo(value: string | ArrayBuffer | null): void {
    const ctrl = this.group.get('headerLogo');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    this.cd.markForCheck();
  }

  private clearLogo(): void {
    this.previewLogo = null;
    this.logoFileName = null;
    if (this.logoFileInputRef?.nativeElement) {
      this.logoFileInputRef.nativeElement.value = '';
    }
    this.patchLogo(null);
  }
}
